import type { QueueOverview as QueueOverviewData } from '@dev-console/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EmptyState, ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getJobQueues } from '@/config/api-helpers';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';
import { CanonicalJobDrawer } from '@/pages/jobs/CanonicalJob';

type SortField = 'canonicalJob' | 'pod' | 'mainQueueCount' | 'dlqCount';
type SortDirection = 'asc' | 'desc';

const SORT_ARROW = { asc: '\u2191', desc: '\u2193' } as const;

function PodPillSelect({
  pods,
  selected,
  onChange,
}: {
  pods: string[];
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function toggle(pod: string) {
    if (selectedSet.has(pod)) {
      onChange(selected.filter((p) => p !== pod));
    } else {
      onChange([...selected, pod]);
    }
  }

  function remove(pod: string) {
    onChange(selected.filter((p) => p !== pod));
  }

  return (
    <div ref={ref} className='relative'>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className='flex min-w-[140px] items-center gap-1.5 rounded-md border border-neutral-200 px-2 py-1 text-sm text-neutral-700 focus:border-neutral-400 focus:outline-none'
      >
        {selected.length > 0 ? (
          <span className='flex flex-wrap items-center gap-1'>
            {selected.map((pod) => (
              <span
                key={pod}
                className='inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700'
              >
                {pod}
                <span
                  role='button'
                  className='ml-0.5 cursor-pointer text-neutral-400 hover:text-neutral-700'
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(pod);
                  }}
                >
                  &times;
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span className='px-1 text-neutral-400'>All pods</span>
        )}
        <svg
          className='ml-auto size-4 shrink-0 text-neutral-400'
          fill='none'
          stroke='currentColor'
          strokeWidth={2}
          viewBox='0 0 24 24'
        >
          <path d='m6 9 6 6 6-6' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      </button>
      {open && (
        <div className='absolute z-10 mt-1 min-w-full rounded-md border border-neutral-200 bg-white py-1 shadow-lg'>
          {selected.length > 0 && (
            <button
              type='button'
              onClick={() => {
                onChange([]);
                setOpen(false);
              }}
              className='w-full px-3 py-1.5 text-left text-sm text-neutral-600 hover:bg-neutral-50'
            >
              Clear all
            </button>
          )}
          {pods.map((pod) => (
            <button
              key={pod}
              type='button'
              onClick={() => toggle(pod)}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
                selectedSet.has(pod)
                  ? 'bg-neutral-50 font-medium text-neutral-900'
                  : 'text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              <span
                className={`flex size-4 shrink-0 items-center justify-center rounded border ${
                  selectedSet.has(pod) ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300'
                }`}
              >
                {selectedSet.has(pod) && (
                  <svg className='size-3' fill='none' stroke='currentColor' strokeWidth={3} viewBox='0 0 24 24'>
                    <path d='m5 13 4 4L19 7' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                )}
              </span>
              {pod}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const JobNamesList = ({ jobNames }: { jobNames: string[] }) => {
  if (jobNames.length === 0) return <span className='text-sm text-neutral-400'>—</span>;

  return (
    <div className='flex flex-wrap gap-1'>
      {jobNames.map((name) => (
        <span
          key={name}
          className='inline-block rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700'
        >
          {name}
        </span>
      ))}
    </div>
  );
};

const queueParamDefs = {
  search: { default: '' },
  pods: { default: [] as string[] },
  dlqOnly: { default: false },
  queue: { default: '' },
};

export const QueueOverview = () => {
  const [queues, setQueues] = useState<QueueOverviewData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('canonicalJob');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');

  // Filter / sort state — synced to URL query params
  const {
    search: [search, setSearch],
    pods: [podFilters, setPodFilters],
    dlqOnly: [dlqOnly, setDlqOnly],
    queue: [selectedQueueParam, setSelectedQueueParam],
  } = useSearchParamsState(queueParamDefs);

  const openQueueDetails = useCallback(
    (queue: QueueOverviewData) => {
      setSelectedQueueId(queue.canonicalJob);
      setSelectedQueueParam(queue.canonicalJob);
    },
    [setSelectedQueueParam]
  );

  const closeQueueDetails = useCallback(() => {
    setSelectedQueueId(null);
    setSelectedQueueParam('');
  }, [setSelectedQueueParam]);

  const loadQueues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getJobQueues();
      setQueues(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueues();
  }, [loadQueues]);

  const pods = useMemo(() => {
    const set = new Set(queues.map((q) => q.pod));
    return Array.from(set).sort();
  }, [queues]);

  const selectedQueue = useMemo(() => {
    const canonicalJob = selectedQueueId ?? selectedQueueParam;
    if (!canonicalJob) return null;
    return queues.find((queue) => queue.canonicalJob === canonicalJob) ?? null;
  }, [queues, selectedQueueId, selectedQueueParam]);

  const podFilterSet = useMemo(() => new Set(podFilters), [podFilters]);

  const filteredAndSorted = useMemo(() => {
    const term = search.toLowerCase();
    let result = queues;

    if (term) {
      result = result.filter(
        (q) => q.canonicalJob.toLowerCase().includes(term) || q.jobNames.some((n) => n.toLowerCase().includes(term))
      );
    }

    if (podFilterSet.size > 0) {
      result = result.filter((q) => podFilterSet.has(q.pod));
    }

    if (dlqOnly) {
      result = result.filter((q) => q.dlqCount > 0);
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...result].sort((a, b) => {
      if (sortField === 'mainQueueCount' || sortField === 'dlqCount') {
        return (a[sortField] - b[sortField]) * dir;
      }
      return a[sortField].localeCompare(b[sortField]) * dir;
    });
  }, [queues, search, podFilterSet, dlqOnly, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'mainQueueCount' || field === 'dlqCount' ? 'desc' : 'asc');
    }
  };

  const dlqJobCount = useMemo(() => queues.filter((q) => q.dlqCount > 0).length, [queues]);

  if (loading) {
    return <LoadingCard message='Loading queues…' />;
  }

  if (error != null) {
    return <ErrorBanner message={error} onRetry={loadQueues} />;
  }

  const sortIndicator = (field: SortField) => (sortField === field ? ` ${SORT_ARROW[sortDir]}` : '');

  const thButton = (field: SortField, label: string, align?: 'right') => (
    <th className={`font-mediumbold px-4 py-2.5 text-neutral-700 ${align === 'right' ? 'text-right' : ''}`}>
      <button onClick={() => handleSort(field)} className='inline-flex items-center gap-1 hover:text-neutral-900'>
        {label}
        <span className='text-xs text-neutral-400'>{sortIndicator(field)}</span>
      </button>
    </th>
  );

  return (
    <div>
      {/* Toolbar */}
      <div className='mb-4 flex flex-wrap items-center gap-3'>
        <input
          type='text'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search jobs...'
          className='w-64 rounded-md border border-neutral-200 px-3 py-1.5 text-sm placeholder:text-neutral-400 focus:border-neutral-400 focus:outline-none'
        />

        <PodPillSelect pods={pods} selected={podFilters} onChange={setPodFilters} />

        <label
          className='flex items-center gap-1.5 text-sm text-neutral-700'
          title='Show only queues that currently have dead-letter messages'
        >
          <input
            type='checkbox'
            checked={dlqOnly}
            onChange={(e) => setDlqOnly(e.target.checked)}
            className='rounded border-neutral-300'
          />
          Only queues with DLQ messages
          {dlqJobCount > 0 && (
            <span className='rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700'>
              {dlqJobCount}
            </span>
          )}
        </label>

        <div className='ml-auto flex items-center gap-3'>
          <span className='text-sm text-neutral-500'>
            {filteredAndSorted.length} of {queues.length} queues
          </span>
          <button
            onClick={loadQueues}
            className='rounded-md border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50'
          >
            Refresh
          </button>
        </div>
      </div>

      {filteredAndSorted.length === 0 ? (
        <EmptyState message={queues.length === 0 ? 'No queues found.' : 'No queues match the current filters.'} />
      ) : (
        <div className='overflow-x-auto rounded-md border border-neutral-200'>
          <table className='w-full text-left text-sm'>
            <thead className='border-b border-neutral-200 bg-neutral-50'>
              <tr>
                {thButton('canonicalJob', 'Canonical Job')}
                {thButton('pod', 'Pod')}
                <th className='font-mediumbold px-4 py-2.5 text-neutral-700'>Job Names</th>
                {thButton('mainQueueCount', 'Main Queue', 'right')}
                {thButton('dlqCount', 'DLQ', 'right')}
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((queue) => (
                <tr
                  key={queue.canonicalJob}
                  className='hover:bg-neutral-25 cursor-pointer border-b border-neutral-100'
                  onClick={() => openQueueDetails(queue)}
                >
                  <td className='px-4 py-2.5 font-mono text-xs'>
                    <button
                      className='text-neutral-800 hover:text-neutral-900 hover:underline'
                      onClick={(event) => {
                        event.stopPropagation();
                        openQueueDetails(queue);
                      }}
                      type='button'
                    >
                      {queue.canonicalJob}
                    </button>
                  </td>
                  <td className='px-4 py-2.5 text-neutral-600'>{queue.pod}</td>
                  <td className='px-4 py-2.5'>
                    <JobNamesList jobNames={queue.jobNames} />
                  </td>
                  <td className='px-4 py-2.5 text-right tabular-nums text-neutral-700'>{queue.mainQueueCount}</td>
                  <td className='px-4 py-2.5 text-right tabular-nums'>
                    {queue.dlqCount > 0 ? (
                      <button
                        className='font-medium text-red-600 hover:text-red-800 hover:underline'
                        onClick={(event) => {
                          event.stopPropagation();
                          openQueueDetails(queue);
                        }}
                        type='button'
                      >
                        {queue.dlqCount}
                      </button>
                    ) : (
                      <span className='text-neutral-500'>0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedQueue != null && (
        <CanonicalJobDrawer
          queue={selectedQueue}
          onClose={closeQueueDetails}
          onQueueUpdated={() => {
            loadQueues();
          }}
        />
      )}
    </div>
  );
};
